import React, { useState } from 'react';
import axios from 'axios';
import { X, Eye, EyeSlash, Key } from '@phosphor-icons/react';
import { formatApiErrorDetail } from '../auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Self-service password change modal — used from the sidebar by any signed-in
 * user. Wraps the existing /api/auth/change-password endpoint with a 3-field
 * form (current / new / confirm) plus show/hide toggles and inline validation.
 *
 * Dismissible (unlike CustomerConfidentialityModal). On success shows a brief
 * confirmation and closes itself after a short delay so the user sees feedback.
 */
const ChangePasswordModal = ({ open, onClose }) => {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(false);

  if (!open) return null;

  const reset = () => {
    setCurrentPw(''); setNewPw(''); setConfirmPw('');
    setShowCurrent(false); setShowNew(false);
    setBusy(false); setErr(null); setOk(false);
  };

  const close = () => { reset(); onClose?.(); };

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr(null);
    if (newPw.length < 8) { setErr('New password must be at least 8 characters.'); return; }
    if (newPw !== confirmPw) { setErr('New password and confirmation do not match.'); return; }
    if (newPw === currentPw) { setErr('New password must be different from current.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPw,
        new_password: newPw,
      });
      setOk(true);
      // Brief delay so user sees the confirmation, then dismiss.
      setTimeout(close, 1400);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[65] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="change-password-modal"
      onClick={close}
    >
      <div
        className="bg-surface border border-border-color rounded-xl w-full max-w-md overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 p-5 border-b border-border-color">
          <Key size={22} weight="duotone" className="text-gold shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-text-primary" data-testid="change-password-modal-title">
              Change password
            </h3>
            <p className="text-xs text-text-secondary mt-0.5">
              Set a new password. Minimum 8 characters, must differ from current.
            </p>
          </div>
          <button
            onClick={close}
            data-testid="change-password-modal-close"
            className="text-text-secondary hover:text-text-primary"
            aria-label="Close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <form className="p-5 space-y-4" onSubmit={submit} data-testid="change-password-form">
          <div>
            <label className="text-xs text-text-secondary mb-1 block">Current password</label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="change-password-current"
                className="w-full px-3 py-2 pr-10 text-sm bg-background border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-gold"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary hover:text-gold"
                tabIndex={-1}
                aria-label={showCurrent ? 'Hide current password' : 'Show current password'}
              >
                {showCurrent ? <EyeSlash size={16} weight="duotone" /> : <Eye size={16} weight="duotone" />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-text-secondary mb-1 block">New password (min 8 characters)</label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="change-password-new"
                className="w-full px-3 py-2 pr-10 text-sm bg-background border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-gold"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary hover:text-gold"
                tabIndex={-1}
                aria-label={showNew ? 'Hide new password' : 'Show new password'}
              >
                {showNew ? <EyeSlash size={16} weight="duotone" /> : <Eye size={16} weight="duotone" />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-text-secondary mb-1 block">Confirm new password</label>
            <input
              type={showNew ? 'text' : 'password'}
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              data-testid="change-password-confirm"
              className="w-full px-3 py-2 text-sm bg-background border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-gold"
            />
          </div>
          {err && (
            <p
              className="text-xs text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2"
              data-testid="change-password-error"
            >
              {err}
            </p>
          )}
          {ok && (
            <p
              className="text-xs text-success bg-success/10 border border-success/30 rounded px-3 py-2"
              data-testid="change-password-success"
            >
              Password updated successfully.
            </p>
          )}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={close}
              disabled={busy}
              data-testid="change-password-cancel"
              className="flex-1 px-4 py-2 text-sm border border-border-color text-text-secondary rounded-lg hover:border-gold hover:text-gold disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || ok || !currentPw || !newPw || !confirmPw}
              data-testid="change-password-submit"
              className="flex-1 px-4 py-2 text-sm font-bold bg-gold text-black rounded-lg hover:bg-gold-hover disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
